import type {Attribute, Designation} from 'src/types/field-mapping';
import useI18n from 'src/hooks/useI18n';
import * as React from 'react';
import {Select} from 'src/designSystem2021Components/select';
import type {SelectOption} from 'src/designSystem2021Components/select';
import {Smallest, Clickable} from 'src/designSystem2021Components/text-v2';
import {MouseTip} from 'src/components/lib/mouse-tip/mouse-tip';
import {useDispatch} from 'react-redux';
import {updateLocalStandardEntityDesignation} from 'src/action-creators/field-mapping';

import WarningIcon from 'src/images/icons/alert-modal.svg?noAttrs';
import DoneIcon from 'src/images/icons/done_all.svg?noAttrs';
import css from './field-selection-table.css';


type standardEntityDesignationsProps = {
  standardEntityType: string;
  designations: Designation[];
  entityAttributesList?: Attribute[];
  entityIndex: number;
  updateSectionLevelError: (index: number, error: boolean) => void;
};

const StandardEntityTypeSection = ({
  standardEntityType,
  designations,
  entityAttributesList,
  entityIndex,
  updateSectionLevelError,
}: standardEntityDesignationsProps): React.ReactElement<
  React.ComponentProps<'div'>
> => {
  const {labelI18nInstance} = useI18n();

  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const [selectedOptionsCountMap, setSelectedOptionsCountMap] = React.useState(
    {},
  );
  React.useEffect(() => {
    const selectedOptionsCountMapInitial: Record<string, any> = {};
    for (const designation of designations) {
      if (designation.entity_attribute_id) {
        if (!selectedOptionsCountMapInitial[designation.entity_attribute_id]) {
          selectedOptionsCountMapInitial[designation.entity_attribute_id] = 1;
        } else {
          selectedOptionsCountMapInitial[designation.entity_attribute_id] =
            selectedOptionsCountMapInitial[designation.entity_attribute_id] + 1;
        }
      }
    }
    setSelectedOptionsCountMap(selectedOptionsCountMapInitial);
  }, [designations]);

  const fieldLevelErrorMap = React.useRef([false]);
  const updateFieldLevelErrorMap = (
    index: number,
    error: undefined | boolean,
  ) => {
    // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
    fieldLevelErrorMap.current[index] = error;
    let orWithInitial = false;
    orWithInitial = fieldLevelErrorMap.current.reduce(
      (previousValue, currentValue) => previousValue || currentValue,
      false,
    );
    updateSectionLevelError(entityIndex, !!orWithInitial);
  };

  const setSelectedAttribute = (
    option: SelectOption<string> | null | undefined,
    designationName: string,
    entityAttributeId: string | null | undefined,
  ) => {
    updateSelectedOptionsCountMap(
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'null | undefined'.
      option?.value,
      designationName,
      entityAttributeId,
    );
    dispatch(
      updateLocalStandardEntityDesignation({
        standard_entity_type: standardEntityType,
        designation_name: designationName,
        entity_attribute_name: option?.label || null,
        entity_attribute_id: option?.value || null,
      }),
    );
  };
  const updateSelectedOptionsCountMap = (
    newEntityAttributeId = null,
    designationName: string,
    entityAttributeId: string | null | undefined,
  ) => {
    if (entityAttributeId === newEntityAttributeId) {
      return;
    }
    const selectedOptionsCountMapTemp = {...selectedOptionsCountMap} as const;
    // decrease count of previously selected entity
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
    if (entityAttributeId && selectedOptionsCountMapTemp[entityAttributeId]) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      selectedOptionsCountMapTemp[entityAttributeId] =
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        selectedOptionsCountMapTemp[entityAttributeId] - 1;
    }

    // Increase count of newly selected entity
    if (newEntityAttributeId) {
      if (!selectedOptionsCountMapTemp[newEntityAttributeId]) {
        // @ts-expect-error - TS2322 - Type '1' is not assignable to type 'never'.
        selectedOptionsCountMapTemp[newEntityAttributeId] = 1;
      } else {
        // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'never'.
        selectedOptionsCountMapTemp[newEntityAttributeId] =
          selectedOptionsCountMapTemp[newEntityAttributeId] + 1;
      }
    }
    setSelectedOptionsCountMap(selectedOptionsCountMapTemp);
  };

  const checkError = (
    selectedEntityAttributeId: string | null | undefined,
    designationIndex: number,
  ) => {
    let errorState;
    if (
      selectedEntityAttributeId &&
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      selectedOptionsCountMap[selectedEntityAttributeId] === 1
    ) {
      errorState = false;
    } else if (
      selectedEntityAttributeId &&
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      selectedOptionsCountMap[selectedEntityAttributeId] > 1
    ) {
      errorState = true;
    }
    updateFieldLevelErrorMap(designationIndex, errorState);
    return errorState;
  };

  const filterOptions = (
    options: Array<Attribute>,
    dataType: string,
  ): SelectOption<string>[] => {
    const filterredAttributes = options.filter(
      (option) => option.data_type === dataType,
    );
    const mapAttributeArrayAsOptions = filterredAttributes.map(
      (attributeObj) => ({
        label: attributeObj.attribute_name,
        value: attributeObj.attribute_id,
      }),
    );
    return mapAttributeArrayAsOptions;
  };

  return (
    <div className={css.entityTypeBox}>
      <Smallest className={css.entityType}>{standardEntityType}</Smallest>
      {designations.map((designationObj, designationIndex) => (
        <div className={css.attributeRow} key={designationObj.designation_name}>
          <div className={css.attributeNameBox}>
            <Clickable className={css.attributeNameText}>
              {designationObj.designation_name}
            </Clickable>
          </div>
          {entityAttributesList && designationObj && (
            <>
              {checkError(
                designationObj.entity_attribute_id,
                designationIndex,
              ) ? (
                <MouseTip
                  content={
                    <>
                      {`${t(
                        'Same_ATS_field_is_mapped_to_one_or_more_attributes',
                        'Same ATS field is mapped to one or more attributes',
                      )}. ${t(
                        'Please_check_and_resolve',
                        'Please check and resolve',
                      )}.`}
                    </>
                  }
                  fixedTo="top"
                >
                  <div className={css.selectWrapBox}>
                    {/* @ts-expect-error - TS2739 - Type '{ options: SelectOption<string>[]; value: { label: string; value: string; }; placeholder: string; onChange: (option: { label: string; value: string; } | null | undefined) => void; classNames: { ...; }; error: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ label: string; value: string; }>': resolveKey, resolveLabel */}
                    <Select
                      options={filterOptions(
                        entityAttributesList,
                        designationObj.data_type,
                      )}
                      value={{
                        label: designationObj.entity_attribute_name || '',
                        value: designationObj.entity_attribute_id || '',
                      }}
                      placeholder={t('Select_Field', 'Select Field')}
                      onChange={(option) =>
                        setSelectedAttribute(
                          option,
                          designationObj.designation_name,
                          designationObj.entity_attribute_id,
                        )
                      }
                      classNames={{
                        container: css.selectContainer,
                        box: css.selectBox,
                      }}
                      error={true}
                    />
                    <div className={css.placeholder}></div>
                  </div>
                </MouseTip>
              ) : (
                <div className={css.selectWrapBox}>
                  {/* @ts-expect-error - TS2739 - Type '{ options: SelectOption<string>[]; value: { label: string; value: string; }; placeholder: string; onChange: (option: { label: string; value: string; } | null | undefined) => void; classNames: { ...; }; error: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ label: string; value: string; }>': resolveKey, resolveLabel */}
                  <Select
                    options={filterOptions(
                      entityAttributesList,
                      designationObj.data_type,
                    )}
                    value={{
                      label: designationObj.entity_attribute_name || '',
                      value: designationObj.entity_attribute_id || '',
                    }}
                    placeholder={t('Select_Field', 'Select Field')}
                    onChange={(option) =>
                      setSelectedAttribute(
                        option,
                        designationObj.designation_name,
                        designationObj.entity_attribute_id,
                      )
                    }
                    classNames={{
                      container: css.selectContainer,
                      box: css.selectBox,
                    }}
                    error={false}
                  />
                  {designationObj.entity_attribute_id ? (
                    // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
                    <DoneIcon className={css.doneIcon} />
                  ) : (
                    // @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'.
                    <WarningIcon className={css.warningIcon} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default StandardEntityTypeSection;
